import { Transfer } from '@varo/graphql-schema-types';
import { formatDate } from 'date-fns/format';
import { useRouter } from 'expo-router';
import useTransferHistory from 'features/moveMoney/screens/transferHistory/hooks/useTransferHistory';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import {
  getLast4Digits,
  transferStatusTag,
} from 'shared/components/TransferHistory/transfer.helper';
import TransferHistoryWrapper from 'shared/components/TransferHistory/TransferHistoryWrapper';
import { TagProps } from 'shared/components/vxl/Tag/Tag';
import { EmptyStateScreen } from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import theme from 'shared/styles/theme';
import { capitalizeWord } from 'shared/utils/string/string.helpers';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

const TransferHistory = () => {
  const router = useRouter();
  const {
    data: transferList = [],
    error,
    loading,
    loadMore,
  } = useTransferHistory();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferHistory',
  });

  const flow = useVaroOnboardingFlow();

  const titleTextString = (transfer: Transfer) =>
    `${translatedText('from')}${transfer.sourceAccount.nickname} • ${getLast4Digits(
      transfer?.sourceAccount?.accountNumber || '',
    )}`;

  const secondaryTextString = (transfer: Transfer) =>
    `${formatDate(transfer.createdAt, 'MMM dd')} • ${translatedText('to')} ${transfer?.destinationAccount.nickname} • ${getLast4Digits(transfer?.destinationAccount?.accountNumber || '')}`;

  const renderTags = (transfer: Transfer): TagProps[] => [
    {
      text: capitalizeWord(transfer.status),
      type: transferStatusTag(transfer.status),
    },
  ];

  return (
    <TransferHistoryWrapper
      title={translatedText('transferHistoryTitle')}
      transferList={transferList}
      error={!!error}
      loading={loading}
      loadMore={loadMore}
      onPressItem={(item) => {
        router.push({
          pathname: `/${flow}/transfer/transfer-details`,
          params: { transferId: item.id },
        });
      }}
      renderTitle={titleTextString}
      renderSecondaryText={secondaryTextString}
      renderTags={renderTags}
      renderEmptyState={() => {
        return (
          <View style={styles.emptyWrapper}>
            <EmptyStateScreen
              bodyText={translatedText('emptyStateBodyText')}
              headlineText={translatedText('emptyStateHeadlineText')}
              illustrationKey="BANK_A"
              actionButtonText={translatedText('emptyStateButtonLabel')}
              actionOnPress={() =>
                router.replace('/move-money/transfer/transfer-money')
              }
            />
          </View>
        );
      }}
    />
  );
};

const styles = StyleSheet.create({
  emptyWrapper: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default TransferHistory;
